import React, { useState } from "react";
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Paper,
    Box,
    Typography,
    Menu,
    MenuItem,
    Chip,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { SiteUser, User } from "../../types";
import EditUserModal from "../modals/EditUserModal";
import AssignTrialsModal from "../modals/AssignTrialsModal";
import EditRolesModal from "../modals/EditRolesModal";
import theme from "../../../../app/theme";
import { useAssignGroupsToUserMutation, useEditUserRolesMutation, useUpdateUserMutation } from "../../dataAccess";
import { useDispatch } from "react-redux";
import { updateUser } from "../../userManagementSlice";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    fontSize: theme.spacing(12),
}));

interface UsersTableProps {
    users: User[];
    totalCount: number;
}

const UsersTable: React.FC<UsersTableProps> = ({ users, totalCount }) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [selectedUser, setSelectedUser] = useState<User | null>(null);
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [assignModalOpen, setAssignModalOpen] = useState(false);
    const [rolesModalOpen, setRolesModalOpen] = useState(false);
    const [updateUserRequest] = useUpdateUserMutation();
    const [assignGroupsToUserRequest] = useAssignGroupsToUserMutation();
    const [editUserRolesRequest] = useEditUserRolesMutation();
    const dispatch = useDispatch();

    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, user: User) => {
        setAnchorEl(event.currentTarget);
        setSelectedUser(user);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleEditUser = () => {
        setEditModalOpen(true);
        handleMenuClose();
    };

    const handleEditModalClose = () => {
        setEditModalOpen(false);
        setSelectedUser(null);
    };

    const handleEditUserSave = async (user: User | SiteUser) => {
        const result = await updateUserRequest({ userId: user.uuid, user });
        if (result && "data" in result) {
            dispatch(updateUser({ ...user, fullName: `${user.firstName} ${user.lastName}` } as User));
        }
        setEditModalOpen(false);
        setSelectedUser(null);
    };

    const handleAssignTrials = () => {
        setAssignModalOpen(true);
        handleMenuClose();
    };

    const handleAssignModalClose = () => {
        setAssignModalOpen(false);
        setSelectedUser(null);
    };

    const handleAssignSave = async (userId: string, newTrialIds: string[]) => {
        const result = await assignGroupsToUserRequest({ userId, affiliationType: "trial", assignTo: newTrialIds });
        if (result && "data" in result) {
            const transformedTrials = result.data.map((trial) => ({
                trialName: trial.name || "",
                uuid: trial.uuid,
                related: trial.related,
            }));
            dispatch(updateUser({ ...selectedUser, trials: transformedTrials } as User));
        }
        setAssignModalOpen(false);
        setSelectedUser(null);
    };

    const handleEditRoles = () => {
        setRolesModalOpen(true);
        handleMenuClose();
    };

    const handleRolesModalClose = () => {
        setRolesModalOpen(false);
        setSelectedUser(null);
    };

    const handleRolesSave = async (userId: string, newRoles: string[]) => {
        const result = await editUserRolesRequest({ userId, roles: newRoles });
        if (result && "data" in result) {
            if (selectedUser) {
                dispatch(updateUser({ ...selectedUser, roles: newRoles } as User));
            }
        }
        setRolesModalOpen(false);
        setSelectedUser(null);
    };

    return (
        <Box>
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", mb: 2, position: "relative" }}>
                <Typography variant="h6">Trial Users</Typography>
                <Box sx={{ position: "absolute", right: 0 }}>
                    <Typography variant="body2" color="textSecondary">
                        {totalCount} total
                    </Typography>
                </Box>
            </Box>
            <TableContainer component={Paper} sx={{ overflowX: "auto", width: "100%" }}>
                <Table sx={{ width: "100%", tableLayout: "fixed", bgcolor: theme.palette.background.default }}>
                    <TableHead>
                        <TableRow>
                            <StyledTableCell sx={{ width: "25%" }}>Email</StyledTableCell>
                            <StyledTableCell sx={{ width: "20%" }}>Name</StyledTableCell>
                            <StyledTableCell sx={{ width: "15%" }}>Activated</StyledTableCell>
                            <StyledTableCell sx={{ display: { xs: "none", sm: "table-cell" }, width: "15%" }}>
                                Expires On
                            </StyledTableCell>
                            <StyledTableCell sx={{ display: { xs: "none", md: "table-cell" }, width: "20%" }}>
                                Roles
                            </StyledTableCell>
                            <StyledTableCell sx={{ display: { xs: "none", lg: "table-cell" }, width: "20%" }}>
                                Trials
                            </StyledTableCell>
                            <StyledTableCell sx={{ width: "5%" }} align="right"></StyledTableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {users.map((user) => (
                            <TableRow hover key={user.email}>
                                <StyledTableCell sx={{ color: "#fff" }}>{user.email}</StyledTableCell>
                                <StyledTableCell sx={{ color: "#fff" }}>{user.fullName}</StyledTableCell>
                                <StyledTableCell>
                                    <Box
                                        sx={{
                                            display: "inline-block",
                                            px: 5,
                                            py: 0.5,
                                            borderRadius: "4px",
                                            border: user.activated ? "1px solid #4caf50" : "1px solid #666",
                                            bgcolor: user.activated
                                                ? "rgba(76, 175, 80, 0.15)"
                                                : "rgba(255, 255, 255, 0.08)",
                                            color: user.activated ? "#4caf50" : "#999",
                                            fontSize: "0.75rem",
                                            fontWeight: 500,
                                        }}
                                    >
                                        {user.activated ? "Active" : "Inactive"}
                                    </Box>
                                </StyledTableCell>
                                <StyledTableCell sx={{ color: "#fff", display: { xs: "none", sm: "table-cell" } }}>
                                    {new Date(user.accessExpires).toLocaleDateString()}
                                </StyledTableCell>
                                <StyledTableCell sx={{ display: { xs: "none", md: "table-cell" } }}>
                                    <Box display="flex" flexWrap="wrap" gap={0.5}>
                                        {user.roles && user.roles.length > 0 ? (
                                            user.roles.map((role, index) => (
                                                <Chip
                                                    key={`${user.email}-role-${index}`}
                                                    label={typeof role === "string" ? role : JSON.stringify(role)}
                                                    size="small"
                                                    sx={{
                                                        bgcolor: "rgba(76, 175, 80, 0.15)",
                                                        color: "#4caf50",
                                                        border: "1px solid #4caf50",
                                                        margin: "1px",
                                                    }}
                                                />
                                            ))
                                        ) : (
                                            <Typography variant="caption" sx={{ color: "#aaa" }}>
                                                -
                                            </Typography>
                                        )}
                                    </Box>
                                </StyledTableCell>
                                <StyledTableCell sx={{ display: { xs: "none", lg: "table-cell" } }}>
                                    <Box display="flex" flexWrap="wrap" gap={0.5}>
                                        {user.trials && user.trials.length > 0 ? (
                                            user.trials.map((trial, index) => (
                                                <Chip
                                                    key={`${user.email}-trial-${index}`}
                                                    label={
                                                        typeof trial === "object" && trial?.trialName
                                                            ? trial.trialName
                                                            : typeof trial === "string"
                                                            ? trial
                                                            : "-"
                                                    }
                                                    size="small"
                                                    sx={{
                                                        bgcolor: "rgba(76, 175, 80, 0.15)",
                                                        color: "#4caf50",
                                                        border: "1px solid #4caf50",
                                                        margin: "1px",
                                                    }}
                                                />
                                            ))
                                        ) : (
                                            <Typography variant="caption" sx={{ color: "#aaa" }}>
                                                -
                                            </Typography>
                                        )}
                                    </Box>
                                </StyledTableCell>
                                <StyledTableCell align="right">
                                    <IconButton
                                        size="small"
                                        sx={{ color: "#888" }}
                                        onClick={(e) => handleMenuOpen(e, user)}
                                    >
                                        <MoreVertIcon fontSize="small" />
                                    </IconButton>
                                </StyledTableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                PaperProps={{ sx: { bgcolor: "#222", color: "#fff" } }}
            >
                <MenuItem onClick={handleEditUser}>Edit User</MenuItem>
                <MenuItem onClick={handleAssignTrials}>Assign User</MenuItem>
                <MenuItem onClick={handleEditRoles}>Edit Roles</MenuItem>
            </Menu>
            <EditUserModal
                open={editModalOpen}
                user={selectedUser}
                onClose={handleEditModalClose}
                onSave={handleEditUserSave}
            />
            <AssignTrialsModal
                open={assignModalOpen}
                user={selectedUser}
                onClose={handleAssignModalClose}
                onSave={handleAssignSave}
            />
            <EditRolesModal
                open={rolesModalOpen}
                user={selectedUser}
                onClose={handleRolesModalClose}
                onSave={handleRolesSave}
            />
        </Box>
    );
};

export default UsersTable;
